import { computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { environment } from '../../environments/environment';
import { UserStore } from './user.store';
import {
  DEFAULT_NOTIFICATION_POPUP_SCOPE,
  isNotificationPopupScope,
  NotificationPopupScope,
} from '../utils/notification-popup-scope';
import {
  AiScribeDefaultRecordingType,
  DEFAULT_AI_SCRIBE_DEFAULT_RECORDING_TYPE,
  isAiScribeDefaultRecordingType,
} from '../utils/recording-type';
import {
  CaseDefaultView,
  DEFAULT_CASE_DEFAULT_VIEW,
  isCaseDefaultView,
} from '../utils/case-default-view';
import {
  DEFAULT_DOCUMENT_EDITOR_EDIT_LAYOUT,
  DEFAULT_DOCUMENT_EDITOR_EDIT_ZOOM,
  DocumentEditorEditLayout,
  DocumentEditorEditZoom,
  isDocumentEditorEditLayout,
  isDocumentEditorEditZoom,
} from '../utils/document-editor-edit';

/**
 * The effective customization for the current user, as resolved by
 * `GET /customization/me`. Keys are snake_case to match the API.
 */
export interface EffectiveCustomizationConfig {
  notification_popup_scope: NotificationPopupScope;
  ai_scribe_default_recording_type: AiScribeDefaultRecordingType;
  case_default_view: CaseDefaultView;
  document_editor_edit_layout: DocumentEditorEditLayout;
  document_editor_edit_zoom: DocumentEditorEditZoom;
}

type CustomizationState = {
  config: EffectiveCustomizationConfig;
  isLoaded: boolean;
  isLoading: boolean;
  /** Organization user the current load belongs to (null if unknown). */
  loadedForUserId: string | null;
};

const DEFAULT_CONFIG: EffectiveCustomizationConfig = {
  notification_popup_scope: DEFAULT_NOTIFICATION_POPUP_SCOPE,
  ai_scribe_default_recording_type: DEFAULT_AI_SCRIBE_DEFAULT_RECORDING_TYPE,
  case_default_view: DEFAULT_CASE_DEFAULT_VIEW,
  document_editor_edit_layout: DEFAULT_DOCUMENT_EDITOR_EDIT_LAYOUT,
  document_editor_edit_zoom: DEFAULT_DOCUMENT_EDITOR_EDIT_ZOOM,
};

const initialState: CustomizationState = {
  config: DEFAULT_CONFIG,
  isLoaded: false,
  isLoading: false,
  loadedForUserId: null,
};

/**
 * Reads each key of an untrusted response, keeping the default for anything
 * missing or unrecognised, so a value the backend adds before the frontend
 * knows it falls back to the default instead of breaking the gate.
 */
function parseEffectiveConfig(response: unknown): EffectiveCustomizationConfig {
  const config =
    response && typeof response === 'object'
      ? (response as { config?: unknown }).config
      : undefined;
  if (!config || typeof config !== 'object') return DEFAULT_CONFIG;

  const raw = config as Partial<
    Record<keyof EffectiveCustomizationConfig, unknown>
  >;
  return {
    notification_popup_scope: isNotificationPopupScope(
      raw.notification_popup_scope,
    )
      ? raw.notification_popup_scope
      : DEFAULT_CONFIG.notification_popup_scope,
    ai_scribe_default_recording_type: isAiScribeDefaultRecordingType(
      raw.ai_scribe_default_recording_type,
    )
      ? raw.ai_scribe_default_recording_type
      : DEFAULT_CONFIG.ai_scribe_default_recording_type,
    case_default_view: isCaseDefaultView(raw.case_default_view)
      ? raw.case_default_view
      : DEFAULT_CONFIG.case_default_view,
    document_editor_edit_layout: isDocumentEditorEditLayout(
      raw.document_editor_edit_layout,
    )
      ? raw.document_editor_edit_layout
      : DEFAULT_CONFIG.document_editor_edit_layout,
    document_editor_edit_zoom: isDocumentEditorEditZoom(
      raw.document_editor_edit_zoom,
    )
      ? raw.document_editor_edit_zoom
      : DEFAULT_CONFIG.document_editor_edit_zoom,
  };
}

/**
 * Effective customization for the logged-in user, loaded once per login.
 * Fails open: until loaded, or if the fetch fails, every switch keeps its
 * built-in default (DEFAULT_CONFIG).
 *
 * Its lifecycle is driven by `AuthTeardownService`, which resets it whenever
 * authentication is cleared, so every login fetches afresh. load() also
 * refetches if a different user is logged in, as a backstop for any teardown
 * path that skips the reset.
 */
export const CustomizationStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(({ config }) => ({
    notificationPopupScope: computed(() => config().notification_popup_scope),
    aiScribeDefaultRecordingType: computed(
      () => config().ai_scribe_default_recording_type,
    ),
    caseDefaultView: computed(() => config().case_default_view),
    documentEditorEditLayout: computed(
      () => config().document_editor_edit_layout,
    ),
    documentEditorEditZoom: computed(() => config().document_editor_edit_zoom),
  })),
  withMethods(
    (store, http = inject(HttpClient), userStore = inject(UserStore)) => {
      // Held outside state so it can be cancelled: a response landing after
      // logout must not become the next user's customization.
      let inFlight: Subscription | undefined;

      function clear(): void {
        inFlight?.unsubscribe();
        inFlight = undefined;
        patchState(store, initialState);
      }

      return {
        load(): void {
          const userId = userStore.organizationUser()?.id ?? null;
          if (userId !== store.loadedForUserId()) {
            clear();
          } else if (store.isLoaded() || store.isLoading()) {
            return;
          }

          patchState(store, { isLoading: true, loadedForUserId: userId });
          inFlight = http
            .get<unknown>(`${environment.api_host}customization/me`)
            .subscribe({
              next: (response) =>
                patchState(store, {
                  config: parseEffectiveConfig(response),
                  isLoaded: true,
                  isLoading: false,
                }),
              error: (error: unknown) => {
                console.warn(
                  'Customization fetch failed; using defaults',
                  (error as { status?: number })?.status,
                );
                patchState(store, { isLoading: false });
              },
            });
        },
        reset(): void {
          clear();
        },
      };
    },
  ),
);
